import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import useSubmitGuard from '../hooks/useSubmitGuard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Plus, Upload, Loader2, CalendarIcon, Truck, Clock,
  TrendingUp, Pencil, Trash2, Settings, X as XIcon, Copy, Download, ChevronDown, ScanLine,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import * as XLSX from 'xlsx';
import { ExportButton } from '../utils/exportUtils';
import { offlineDB } from '../utils/offlineDB';
import ProductionAnalyticsChart from '../components/ProductionAnalyticsChart';
import DeleteRequestDialog from '../components/DeleteRequestDialog';
import PendingDeletionsPanel from '../components/PendingDeletionsPanel';
import SearchableSelect from '../components/SearchableSelect';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek } from 'date-fns';
import { getShiftDate } from '../utils/shiftDate';
import DailyReportTab from './DailyReportTab';
import TripCardScanner from '../components/TripCardScanner';
import {
  FilterBar, DateRangeFilter, SelectFilter, countActiveFilters, filterByDateRange
} from '../components/FilterComponents';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SHIFTS = [
  { id: 'A', label: 'Shift A (4am – 12pm)' },
  { id: 'B', label: 'Shift B (12pm – 8pm)' },
  { id: 'C', label: 'Shift C (8pm – 4am)' },
];

const emptyTripRow = (materialType = '') => ({
  dumper_door_no: '', excavator_door_no: '', distance_km: '',
  start_time: '', end_time: '', trips_count: 1,
  material_type: materialType,
});

const emptyHMRow = () => ({ asset_door_no: '', start_reading: '', end_reading: '' });

// ── Volume preview helper ────────────────────────────────────────────────────
const calcVolume = (materialTypes, materialName, tripsCount) => {
  const mat = materialTypes.find(m => m.name === materialName);
  if (!mat || !tripsCount) return null;
  return parseFloat((mat.cum_per_trip * tripsCount).toFixed(2));
};

// ── Main Component ────────────────────────────────────────────────────────────
const ProductionPage = ({ user }) => {
  const [trips, setTrips] = useState([]);
  const [stats, setStats] = useState(null);
  const [assets, setAssets] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('daily');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dateRange, setDateRange] = useState({ from: null, to: null });

  // Filters
  const [filters, setFilters] = useState({
    dateRange: { from: null, to: null }, shift: '', dumper: '', excavator: '', materialType: ''
  });
  const resetFilters = () => setFilters({ dateRange: { from: null, to: null }, shift: '', dumper: '', excavator: '', materialType: '' });

  const canApprove = user?.is_admin || user?.is_super_admin;
  const guard = useSubmitGuard();
  const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD
  const yesterday = new Date(Date.now() - 86400000).toLocaleDateString('en-CA'); // 1 day back
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [pendingDelRequests, setPendingDelRequests] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);

  const fetchPendingDeletions = async () => {
    if (!canApprove) return;
    try {
      const res = await axios.get(`${API}/delete-requests?status=pending&module=production`, { headers: authHeaders() });
      setPendingDelRequests(res.data);
    } catch {}
  };

  const handleApproveDelete = async (reqId) => {
    try {
      await axios.post(`${API}/delete-requests/${reqId}/approve`, {}, { headers: authHeaders() });
      toast.success('Deletion approved');
      fetchData();
      fetchPendingDeletions();
    } catch (e) { toast.error('Failed to approve'); }
  };

  const handleRejectDelete = async (reqId) => {
    try {
      await axios.post(`${API}/delete-requests/${reqId}/reject`, { note: 'Rejected' }, { headers: authHeaders() });
      toast.success('Request rejected');
      fetchPendingDeletions();
    } catch (e) { toast.error('Failed to reject'); }
  };

  // Modals
  const [isAddTripOpen, setIsAddTripOpen] = useState(false);
  const [isEditTripOpen, setIsEditTripOpen] = useState(false);
  const [editingTrip, setEditingTrip] = useState(null);
  const [isBatchEntryOpen, setIsBatchEntryOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Single trip form
  const [tripForm, setTripForm] = useState({
    date: getShiftDate(), shift: '',
    dumper_door_no: '', excavator_door_no: '', distance_km: '',
    start_time: '', end_time: '', trips_count: 1, material_type: '',
  });

  // Batch entry form — trips + hour meter rows
  const [batchForm, setBatchForm] = useState({
    date: getShiftDate(), shift: '', defaultMaterial: '',
    trips: [emptyTripRow()],
    hour_meter_entries: [],
  });
  const [showHMSection, setShowHMSection] = useState(false);

  // Material type config form
  const [matForm, setMatForm] = useState({ name: '', cum_per_trip: '', color: '#f59e0b' });
  const [editingMat, setEditingMat] = useState(null);
  const [savingMat, setSavingMat] = useState(false);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchMaterialTypes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/production/material-types`, { headers: authHeaders() });
      setMaterialTypes(res.data);
    } catch {
      // Non-critical
    }
  }, []);

  const fetchAssets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/assets`, { headers: authHeaders() });
      setAssets(res.data);
    } catch { /* ignore */ }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let queryParams = '';
      if (activeView === 'daily') {
        queryParams = `date=${format(selectedDate, 'yyyy-MM-dd')}`;
      } else if (activeView === 'weekly') {
        const s = startOfWeek(selectedDate, { weekStartsOn: 1 });
        const e = endOfWeek(selectedDate, { weekStartsOn: 1 });
        queryParams = `start_date=${format(s, 'yyyy-MM-dd')}&end_date=${format(e, 'yyyy-MM-dd')}`;
      } else if (activeView === 'monthly') {
        queryParams = `start_date=${format(startOfMonth(selectedDate), 'yyyy-MM-dd')}&end_date=${format(endOfMonth(selectedDate), 'yyyy-MM-dd')}`;
      } else if (dateRange.from && dateRange.to) {
        queryParams = `start_date=${format(dateRange.from, 'yyyy-MM-dd')}&end_date=${format(dateRange.to, 'yyyy-MM-dd')}`;
      }

      // Pass active shift filter to both trips and stats API calls
      const shiftParam = filters.shift ? `shift=${filters.shift}` : '';
      const fullParams = [queryParams, shiftParam].filter(Boolean).join('&');

      const [tripsRes, statsRes] = await Promise.all([
        axios.get(`${API}/production/trips?${fullParams}`, { headers: authHeaders() }),
        axios.get(`${API}/production/stats?${fullParams}`, { headers: authHeaders() }),
      ]);
      setTrips(tripsRes.data);
      setStats(statsRes.data);
    } catch {
      toast.error('Failed to fetch production data');
    } finally {
      setLoading(false);
    }
  }, [activeView, selectedDate, dateRange, filters.shift]);

  useEffect(() => {
    fetchAssets();
    fetchMaterialTypes();
    fetchData();
    fetchPendingDeletions();
    window.addEventListener('offlineSyncComplete', fetchData);
    return () => window.removeEventListener('offlineSyncComplete', fetchData);
  }, [fetchData, fetchAssets, fetchMaterialTypes]);

  // Auto-open add trip dialog from sidebar quick link
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (location.search.includes('create=1')) {
      setIsAddTripOpen(true);
      navigate('/production', { replace: true });
    }
  }, [location.search]);

  const getDumpers = () => assets.filter(a =>
    a.asset_type?.toLowerCase().includes('truck') || a.asset_type?.toLowerCase().includes('dumper') ||
    a.vehicle_category === 'Trucks'
  );
  const getExcavators = () => assets.filter(a =>
    a.asset_type?.toLowerCase().includes('excavator') || a.vehicle_category === 'Excavators'
  );

  // ── Single trip ────────────────────────────────────────────────────────────
  const handleAddTrip = async (e) => {
    e.preventDefault();
    if (!navigator.onLine) {
      await offlineDB.add({
        module: 'production', displayName: `Production Trip – ${tripForm.dumper_door_no} (${tripForm.date})`,
        endpoint: '/production/trips', method: 'POST', data: tripForm,
      });
      toast.success('Saved locally — will sync when online');
      setIsAddTripOpen(false);
      resetTripForm();
      return;
    }
    guard(async () => {
      try {
        await axios.post(`${API}/production/trips`, tripForm, { headers: authHeaders() });
        toast.success('Trip added!');
        setIsAddTripOpen(false);
        resetTripForm();
        fetchData();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to add trip');
      }
    });
  };

  const handleEditTrip = async (e) => {
    e.preventDefault();
    if (!editingTrip) return;
    guard(async () => {
      try {
        await axios.put(`${API}/production/trips/${editingTrip.id}`, tripForm, { headers: authHeaders() });
        toast.success('Trip updated!');
        setIsEditTripOpen(false);
        setEditingTrip(null);
        resetTripForm();
        fetchData();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to update trip');
      }
    });
  };

  const openEditTrip = (trip) => {
    setEditingTrip(trip);
    setTripForm({
      date: trip.date, shift: trip.shift,
      dumper_door_no: trip.dumper_door_no, excavator_door_no: trip.excavator_door_no,
      distance_km: trip.distance_km?.toString() || '', start_time: trip.start_time,
      end_time: trip.end_time, trips_count: trip.trips_count,
      material_type: trip.material_type || '',
    });
    setIsEditTripOpen(true);
  };

  const handleDeleteTrip = async (trip) => {
    if (canApprove) {
      if (!window.confirm('Delete this trip?')) return;
      try {
        await axios.delete(`${API}/production/trips/${trip.id}`, { headers: authHeaders() });
        toast.success('Trip deleted');
        fetchData();
        fetchPendingDeletions();
      } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    } else {
      setDeleteTarget({ id: trip.id, display: `Trip – ${trip.dumper_door_no} on ${trip.date} (${trip.shift} shift)` });
    }
  };

  const resetTripForm = () => setTripForm({
    date: getShiftDate(), shift: '',
    dumper_door_no: '', excavator_door_no: '', distance_km: '',
    start_time: '', end_time: '', trips_count: 1, material_type: '',
  });

  // ── Batch entry ────────────────────────────────────────────────────────────
  const handleBatchEntry = async (e) => {
    e.preventDefault();
    guard(async () => {
      try {
        const payload = {
          date: batchForm.date,
          shift: batchForm.shift,
          trips: batchForm.trips.filter(t => t.dumper_door_no),
          hour_meter_entries: batchForm.hour_meter_entries.filter(h => h.asset_door_no && h.start_reading !== '' && h.end_reading !== ''),
        };
        const res = await axios.post(`${API}/production/trips/batch`, payload, { headers: authHeaders() });
        const hmSaved = res.data.hour_meters_saved || 0;
        toast.success(`${res.data.created} trips saved${hmSaved > 0 ? ` + ${hmSaved} hour meter entries` : ''}!`);
        setIsBatchEntryOpen(false);
        resetBatchForm();
        fetchData();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to save batch');
      }
    });
  };

  const resetBatchForm = () => {
    setBatchForm({
      date: getShiftDate(), shift: '', defaultMaterial: '',
      trips: [emptyTripRow()], hour_meter_entries: [],
    });
    setShowHMSection(false);
  };

  const applyDefaultMaterial = (mat) => {
    const actualMat = mat === '__per_row__' ? '' : mat;
    setBatchForm(prev => ({
      ...prev, defaultMaterial: actualMat,
      trips: prev.trips.map(t => ({ ...t, material_type: actualMat })),
    }));
  };

  const updateBatchRow = (idx, field, value) => {
    const newTrips = [...batchForm.trips];
    newTrips[idx][field] = value;
    setBatchForm({ ...batchForm, trips: newTrips });
  };

  const duplicateBatchRow = (idx) => {
    const copy = { ...batchForm.trips[idx] };
    const newTrips = [...batchForm.trips];
    newTrips.splice(idx + 1, 0, copy);
    setBatchForm({ ...batchForm, trips: newTrips });
  };

  const updateHMRow = (idx, field, value) => {
    const newHM = [...batchForm.hour_meter_entries];
    newHM[idx][field] = value;
    setBatchForm({ ...batchForm, hour_meter_entries: newHM });
  };

  // ── Material type config ───────────────────────────────────────────────────
  const handleSaveMaterial = async (e) => {
    e.preventDefault();
    guard(async () => {
      setSavingMat(true);
      try {
        if (editingMat) {
          await axios.put(`${API}/production/material-types/${editingMat.id}`, matForm, { headers: authHeaders() });
          toast.success('Material updated');
        } else {
          await axios.post(`${API}/production/material-types`, matForm, { headers: authHeaders() });
          toast.success('Material added');
        }
        setMatForm({ name: '', cum_per_trip: '', color: '#f59e0b' });
        setEditingMat(null);
        fetchMaterialTypes();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed');
      } finally {
        setSavingMat(false);
      }
    });
  };

  const handleDeleteMaterial = async (id) => {
    if (!window.confirm('Delete this material type?')) return;
    try {
      await axios.delete(`${API}/production/material-types/${id}`, { headers: authHeaders() });
      toast.success('Deleted');
      fetchMaterialTypes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  // Material badge color
  const getMaterialStyle = (name) => {
    const mat = materialTypes.find(m => m.name === name);
    if (mat) return { backgroundColor: `${mat.color}22`, color: mat.color, border: `1px solid ${mat.color}55` };
    return {};
  };

  if (loading && !stats) return (
    <div className="flex items-center justify-center py-16">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900" />
    </div>
  );

  // ── Apply filters ─────────────────────────────────────────────────────────
  let filteredTrips = [...trips];
  if (filters.dateRange?.from) filteredTrips = filterByDateRange(filteredTrips, 'date', filters.dateRange);
  if (filters.shift) filteredTrips = filteredTrips.filter(t => t.shift === filters.shift);
  if (filters.materialType) filteredTrips = filteredTrips.filter(t => t.material_type === filters.materialType);
  if (filters.dumper) filteredTrips = filteredTrips.filter(t => t.dumper_door_no === filters.dumper);
  if (filters.excavator) filteredTrips = filteredTrips.filter(t => t.excavator_door_no === filters.excavator);

  const canEdit = user?.is_admin || user?.is_super_admin || user?.permissions?.production?.edit;

  const exportReport = async (type) => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const token = localStorage.getItem('token');
      const { data: s } = await axios.get(
        `${API}/production/daily-summary?date=${dateStr}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const mats = s.material_types || [];
      const round2 = n => Math.round(n * 100) / 100;
      const fmt = n => (typeof n === 'number' ? n.toLocaleString('en-IN', { maximumFractionDigits: 2 }) : n);
      const wb = XLSX.utils.book_new();
      const SHIFT_LABELS = { A: '1st Shift', B: '2nd Shift', C: '3rd Shift' };

      // ── Sheet 1: Production ──────────────────────────────────────────
      const rows = [];
      rows.push([`LSN MINERALS — ${type === 'sccl' ? 'SCCL' : 'INTERNAL'} PRODUCTION REPORT — ${dateStr}`]);
      rows.push([]);
      rows.push(['Date / Context', 'Shift', ...mats, 'Total Trips', 'Volume (m³)']);
      ['A', 'B', 'C'].forEach((shift, idx) => {
        const row = [idx === 0 ? dateStr : '', SHIFT_LABELS[shift]];
        mats.forEach(m => row.push(s.daily.by_shift[shift]?.[m]?.trips || 0));
        row.push(mats.reduce((a, m) => a + (s.daily.by_shift[shift]?.[m]?.trips || 0), 0));
        row.push(round2(mats.reduce((a, m) => a + (s.daily.by_shift[shift]?.[m]?.volume || 0), 0)));
        rows.push(row);
      });
      const totRow = ['Total', ''];
      mats.forEach(m => totRow.push(s.daily.totals[m]?.trips || 0));
      totRow.push(s.daily.grand_total.trips, s.daily.grand_total.volume);
      rows.push(totRow);
      const volRow = ['Quantity (m³)', ''];
      mats.forEach(m => volRow.push(round2(s.daily.totals[m]?.volume || 0)));
      volRow.push('', s.daily.grand_total.volume);
      rows.push(volRow);

      // SCCL: adjusted quantity using cum/trip factors
      if (type === 'sccl' && s.sccl_factors && Object.keys(s.sccl_factors).length > 0) {
        rows.push([]);
        rows.push(['SCCL Adjusted Quantity (cum/trip factors)']);
        const scclRow = ['SCCL Quantity', ''];
        mats.forEach(m => scclRow.push(round2((s.daily.totals[m]?.trips || 0) * (s.sccl_factors[m] || 0))));
        const scclTotal = round2(mats.reduce((a, m) => a + (s.daily.totals[m]?.trips || 0) * (s.sccl_factors[m] || 0), 0));
        scclRow.push('', scclTotal);
        rows.push(scclRow);
        rows.push([]);
        rows.push(['SCCL Factors Used (cum/trip)']);
        mats.forEach(m => rows.push([m, s.sccl_factors[m] || '—']));
      }

      rows.push([]);
      const cumRow = ['Monthly Cumulative', ''];
      mats.forEach(m => cumRow.push(round2(s.monthly.totals[m]?.volume || 0)));
      cumRow.push(s.monthly.grand_total.trips, s.monthly.grand_total.volume);
      rows.push(cumRow);
      rows.push([]);
      rows.push(['WORK STOPPAGES']);
      rows.push(['Context', 'Shift', 'Reason']);
      ['A', 'B', 'C'].forEach(shift => {
        const st = (s.stoppages || []).find(x => x.shift === shift);
        rows.push(['Work Stopped', SHIFT_LABELS[shift], st?.reason || 'Nil']);
      });

      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), 'Production');

      // ── Sheet 2: Diesel Efficiency ────────────────────────────────────
      const d = s.diesel;
      const obMats = mats.filter(m => m.toLowerCase().includes('overburden'));
      const csMats = mats.filter(m => !obMats.includes(m));
      const sumV = ms => ms.reduce((a, m) => a + (s.daily.totals[m]?.volume || 0), 0);
      const obV = sumV(obMats), csV = sumV(csMats), totalVol = round2(obV + csV);
      const obT = obMats.reduce((a, m) => a + (s.daily.totals[m]?.trips || 0), 0);
      const csT = csMats.reduce((a, m) => a + (s.daily.totals[m]?.trips || 0), 0);
      const obElig = round2(obV * d.ob_factor), csElig = round2(csV * d.coal_shale_factor);
      const totalElig = round2(obElig + csElig);
      const variance = round2(totalElig - d.daily_consumed);
      const eligFactor = totalVol > 0 ? round2(totalElig / totalVol) : 0;
      const consumedFactor = totalVol > 0 ? round2(d.daily_consumed / totalVol) : 0;
      const mthObV = obMats.reduce((a, m) => a + (s.monthly.totals[m]?.volume || 0), 0);
      const mthCsV = csMats.reduce((a, m) => a + (s.monthly.totals[m]?.volume || 0), 0);
      const mthTotalElig = round2(mthObV * d.ob_factor + mthCsV * d.coal_shale_factor);
      const mthVariance = round2(mthTotalElig - d.monthly_consumed);
      const mthTotalVol = round2(mthObV + mthCsV);

      const ds = [
        [`DETAILS OF DIESEL CONSUMPTION ON ${dateStr}`], [],
        ['DESCRIPTION', 'OB', 'COAL & SHALE', 'TOTAL'],
        ['TRIPS', obT, csT, s.daily.grand_total.trips],
        ['APPROX QUANTITY (m³)', fmt(obV), fmt(csV), fmt(totalVol)],
        ['DIESEL FACTOR', d.ob_factor, d.coal_shale_factor, '-'],
        ['ELIGIBILITY DIESEL (L)', fmt(obElig), fmt(csElig), fmt(totalElig)],
        ['CONSUMED DIESEL (L)', fmt(d.daily_consumed), '', variance >= 0 ? `${fmt(variance)} SAVED` : `${fmt(Math.abs(variance))} EXCESS`],
        ['ELIGIBILITY FACTOR', eligFactor, 'CONSUMED FACTOR', consumedFactor], [],
        ['CUMULATIVE (Month-to-Date)'],
        ['CUMULATIVE QTY. (m³)', fmt(mthObV), fmt(mthCsV), fmt(mthTotalVol)],
        ['ELIGIBILITY DIESEL (L)', '', '', fmt(mthTotalElig)],
        ['CONSUMED DIESEL (L)', fmt(d.monthly_consumed), '', mthVariance >= 0 ? `${fmt(mthVariance)} SAVED` : `${fmt(Math.abs(mthVariance))} EXCESS`],
        ['ELIGIBILITY FACTOR', round2(mthTotalVol > 0 ? mthTotalElig / mthTotalVol : 0), 'CONSUMED FACTOR', round2(mthTotalVol > 0 ? d.monthly_consumed / mthTotalVol : 0)],
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(ds), 'Diesel Efficiency');

      XLSX.writeFile(wb, `${type === 'sccl' ? 'SCCL' : 'Internal'}_Report_${dateStr}.xlsx`);
      toast.success(`${type === 'sccl' ? 'SCCL' : 'Internal'} report downloaded!`);
    } catch {
      toast.error('Failed to generate report');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Production</h1>
          <p className="text-slate-600 mt-1">Track daily production trips and excavated volume</p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          {(user?.is_admin || user?.is_super_admin) && (
            <Button onClick={() => setIsConfigOpen(true)} variant="outline" size="sm">
              <Settings className="h-4 w-4 mr-2" />
              Configure Materials
            </Button>
          )}
          <Button onClick={() => setIsImportOpen(true)} variant="outline" size="sm">
            <Upload className="h-4 w-4 mr-2" />
            Import Excel
          </Button>
          {/* Export Report dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={exporting} data-testid="export-report-dropdown-btn">
                {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                Export Report
                <ChevronDown className="h-3.5 w-3.5 ml-1.5 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel className="text-xs text-slate-500">
                Report for {format(selectedDate, 'dd MMM yyyy')}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => exportReport('internal')} data-testid="export-internal-btn">
                <Download className="h-4 w-4 mr-2 text-slate-500" />
                Internal Report
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportReport('sccl')} data-testid="export-sccl-btn">
                <Download className="h-4 w-4 mr-2 text-green-600" />
                SCCL Report
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsScannerOpen(true)}
            data-testid="scan-trip-card-btn"
            className="border-blue-200 text-blue-700 hover:bg-blue-50"
          >
            <ScanLine className="h-4 w-4 mr-2" />
            Scan Card
          </Button>
          <Button onClick={() => setIsBatchEntryOpen(true)} variant="outline">
            <Plus className="h-4 w-4 mr-2" />
            Batch Entry
          </Button>
          <Button onClick={() => setIsAddTripOpen(true)} className="bg-slate-900 hover:bg-slate-800">
            <Plus className="h-4 w-4 mr-2" />
            Add Trip
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <PendingDeletionsPanel module="production" requests={pendingDelRequests} onApprove={handleApproveDelete} onReject={handleRejectDelete} />

      {/* Active shift filter indicator */}
      {filters.shift && (
        <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
          <Clock className="h-4 w-4 flex-shrink-0" />
          <span>
            Showing stats for <strong>Shift {filters.shift}</strong> only
            {filters.shift === 'A' ? ' (4:00 AM – 11:59 AM)' : filters.shift === 'B' ? ' (12:00 PM – 7:59 PM)' : ' (8:00 PM – 3:59 AM)'}
          </span>
          <button
            className="ml-auto text-blue-500 hover:text-blue-700 font-medium text-xs underline"
            onClick={() => setFilters(f => ({ ...f, shift: '' }))}
            data-testid="clear-shift-filter-btn"
          >
            Clear
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          {
            label: filters.shift ? `Shift ${filters.shift} – Today` : "Today's Trips",
            value: stats?.daily?.total_trips || 0, icon: Truck, color: 'blue', view: 'daily'
          },
          {
            label: filters.shift ? `Shift ${filters.shift} – Volume` : "Today's Volume",
            value: `${stats?.daily?.total_cum || 0} m³`, icon: TrendingUp, color: 'green', view: 'daily'
          },
          {
            label: filters.shift ? `Shift ${filters.shift} – Month Trips` : 'Monthly Trips',
            value: stats?.monthly?.total_trips || 0, icon: Truck, color: 'purple', view: 'monthly'
          },
          {
            label: filters.shift ? `Shift ${filters.shift} – Month Vol.` : 'Monthly Volume',
            value: `${stats?.monthly?.total_cum || 0} m³`, icon: TrendingUp, color: 'amber', view: 'monthly'
          },
        ].map(card => (
          <div key={card.label}
            className="bg-white rounded-lg border p-4 cursor-pointer hover:shadow-md transition-all"
            onClick={() => setActiveView(card.view)}
            data-testid={`production-stat-${card.view}-${card.label.toLowerCase().replace(/[^a-z]/g,'-')}`}
          >
            <div className="flex items-center gap-3">
              <div className={`p-2 bg-${card.color}-100 rounded-lg`}>
                <card.icon className={`h-5 w-5 text-${card.color}-600`} />
              </div>
              <div>
                <p className="text-sm text-slate-600">{card.label}</p>
                <p className="text-2xl font-bold">{card.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Shift breakdown */}
      <Tabs value={activeView} onValueChange={setActiveView}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="daily">Daily</TabsTrigger>
            <TabsTrigger value="weekly">Weekly</TabsTrigger>
            <TabsTrigger value="monthly">Monthly</TabsTrigger>
            <TabsTrigger value="custom">Custom Range</TabsTrigger>
            <TabsTrigger value="daily_report" data-testid="daily-report-tab-trigger">Daily Report</TabsTrigger>
          </TabsList>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-[200px] justify-start text-left text-sm">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {format(selectedDate, 'PPP')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar mode="single" selected={selectedDate}
                onSelect={(d) => d && setSelectedDate(d)} initialFocus />
            </PopoverContent>
          </Popover>
        </div>

        <TabsContent value="daily" className="mt-4">
          <ShiftBreakdown shifts={stats?.daily?.by_shift} />
        </TabsContent>
        <TabsContent value="weekly" className="mt-4">
          <p className="text-slate-600 text-sm">Week of {format(startOfWeek(selectedDate, { weekStartsOn: 1 }), 'MMM d')} – {format(endOfWeek(selectedDate, { weekStartsOn: 1 }), 'MMM d, yyyy')}</p>
        </TabsContent>
        <TabsContent value="monthly" className="mt-4">
          <p className="text-slate-600 text-sm">{format(selectedDate, 'MMMM yyyy')}</p>
        </TabsContent>
        <TabsContent value="custom" className="mt-4">
          <div className="flex gap-4">
            <div>
              <Label className="text-xs">From</Label>
              <Input type="date" value={dateRange.from ? format(dateRange.from, 'yyyy-MM-dd') : ''}
                onChange={(e) => setDateRange({ ...dateRange, from: new Date(e.target.value) })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input type="date" value={dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : ''}
                onChange={(e) => setDateRange({ ...dateRange, to: new Date(e.target.value) })} className="mt-1" />
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* ── Daily Report Tab ─────────────────────────────────────────────── */}
      {activeView === 'daily_report' && (
        <DailyReportTab selectedDate={format(selectedDate, 'yyyy-MM-dd')} />
      )}

      {/* Filters — hidden on Daily Report tab */}
      {activeView !== 'daily_report' && (<>
      <FilterBar onReset={resetFilters} activeFiltersCount={countActiveFilters(filters)}>
        <DateRangeFilter label="Date Range" value={filters.dateRange}
          onChange={(val) => setFilters({ ...filters, dateRange: val })} />
        <SelectFilter label="Shift" value={filters.shift}
          onChange={(val) => setFilters({ ...filters, shift: val })}
          options={SHIFTS.map(s => ({ value: s.id, label: `Shift ${s.id}` }))} />
        <SelectFilter label="Material Type" value={filters.materialType}
          onChange={(val) => setFilters({ ...filters, materialType: val })}
          options={materialTypes.map(m => ({ value: m.name, label: m.name }))} />
        <SelectFilter label="Dumper" value={filters.dumper}
          onChange={(val) => setFilters({ ...filters, dumper: val })}
          options={[...new Set(trips.map(t => t.dumper_door_no).filter(Boolean))].map(d => ({ value: d, label: d }))} />
        <SelectFilter label="Excavator" value={filters.excavator}
          onChange={(val) => setFilters({ ...filters, excavator: val })}
          options={[...new Set(trips.map(t => t.excavator_door_no).filter(Boolean))].map(d => ({ value: d, label: d }))} />
      </FilterBar>

      {/* Trips Table */}
      <div className="bg-white rounded-lg border shadow-sm">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold">Production Trips ({filteredTrips.length})</h3>
          <ExportButton
            rows={filteredTrips}
            columns={[
              { header: 'Date', key: 'date' }, { header: 'Shift', key: 'shift' },
              { header: 'Material', key: 'material_type' }, { header: 'Dumper', key: 'dumper_door_no' },
              { header: 'Excavator', key: 'excavator_door_no' }, { header: 'Distance (km)', key: 'distance_km' },
              { header: 'Start', key: 'start_time' }, { header: 'End', key: 'end_time' },
              { header: 'Trips', key: 'trips_count' }, { header: 'Volume (m³)', key: r => r.cum_volume?.toFixed(2) },
            ]}
            filename={`production_trips_${new Date().toISOString().slice(0, 10)}`}
            title="Production Trips Report"
          />
        </div>
        {filteredTrips.length === 0 ? (
          <div className="p-8 text-center text-slate-500">No trips match the current filters</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead><TableHead>Shift</TableHead>
                <TableHead>Material</TableHead><TableHead>Dumper</TableHead>
                <TableHead>Excavator</TableHead><TableHead>Dist. (km)</TableHead>
                <TableHead>Time</TableHead>
                <TableHead className="text-right">Trips</TableHead>
                <TableHead className="text-right">Volume (m³)</TableHead>
                {canEdit && <TableHead className="w-20" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTrips.map((trip) => (
                <TableRow key={trip.id}>
                  <TableCell className="text-sm">{trip.date}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      trip.shift === 'A' ? 'bg-blue-100 text-blue-700' :
                      trip.shift === 'B' ? 'bg-green-100 text-green-700' :
                      'bg-purple-100 text-purple-700'}`}>Shift {trip.shift}</span>
                  </TableCell>
                  <TableCell>
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={getMaterialStyle(trip.material_type)}>
                      {trip.material_type || '—'}
                    </span>
                  </TableCell>
                  <TableCell className="font-medium text-sm">{trip.dumper_door_no}</TableCell>
                  <TableCell className="text-sm">{trip.excavator_door_no}</TableCell>
                  <TableCell className="text-sm">{trip.distance_km}</TableCell>
                  <TableCell className="text-sm text-slate-500">{trip.start_time} – {trip.end_time}</TableCell>
                  <TableCell className="text-right font-medium">{trip.trips_count}</TableCell>
                  <TableCell className="text-right font-bold text-green-700">{trip.cum_volume?.toFixed(2)}</TableCell>
                  {canEdit && (
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditTrip(trip)}
                          data-testid={`edit-trip-${trip.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteTrip(trip)}
                          className="text-red-500 hover:text-red-700" data-testid={`delete-trip-${trip.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      </>)} {/* end activeView !== 'daily_report' */}

      {/* ── Configure Material Types Modal ─────────────────────────────── */}
      <Dialog open={isConfigOpen} onOpenChange={setIsConfigOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Configure Material Types
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Existing materials */}
            {materialTypes.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Name</TableHead>
                      <TableHead className="text-right">m³ per Trip</TableHead>
                      <TableHead className="w-20" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {materialTypes.map(mat => (
                      <TableRow key={mat.id}>
                        <TableCell>
                          <span className="flex items-center gap-2">
                            <span className="h-3 w-3 rounded-full flex-shrink-0"
                              style={{ backgroundColor: mat.color }} />
                            <span className="font-medium">{mat.name}</span>
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-mono">{mat.cum_per_trip}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm"
                              onClick={() => { setEditingMat(mat); setMatForm({ name: mat.name, cum_per_trip: mat.cum_per_trip, color: mat.color }); }}
                              data-testid={`edit-mat-${mat.id}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-500"
                              onClick={() => handleDeleteMaterial(mat.id)}
                              data-testid={`delete-mat-${mat.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-slate-500 text-sm text-center py-4">No material types yet. Add one below.</p>
            )}

            {/* Add / Edit material form */}
            <div className="border rounded-lg p-4 bg-slate-50">
              <p className="text-sm font-medium mb-3">{editingMat ? `Editing: ${editingMat.name}` : 'Add New Material Type'}</p>
              <form onSubmit={handleSaveMaterial} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Name</Label>
                    <Input placeholder="e.g. Overburden, Coal, Sand"
                      value={matForm.name}
                      onChange={e => setMatForm({ ...matForm, name: e.target.value })}
                      required className="mt-1"
                      data-testid="mat-name-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Volume per Trip (m³)</Label>
                    <Input type="number" step="0.01" placeholder="e.g. 12.5"
                      value={matForm.cum_per_trip}
                      onChange={e => setMatForm({ ...matForm, cum_per_trip: e.target.value })}
                      required className="mt-1"
                      data-testid="mat-volume-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Badge Color</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={matForm.color}
                      onChange={e => setMatForm({ ...matForm, color: e.target.value })}
                      className="h-8 w-12 rounded cursor-pointer border" />
                    <span className="text-xs text-slate-500">{matForm.color}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={savingMat} size="sm" className="bg-slate-900 hover:bg-slate-800"
                    data-testid="save-mat-btn">
                    {savingMat ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                    {editingMat ? 'Update' : 'Add Material'}
                  </Button>
                  {editingMat && (
                    <Button type="button" size="sm" variant="outline"
                      onClick={() => { setEditingMat(null); setMatForm({ name: '', cum_per_trip: '', color: '#f59e0b' }); }}>
                      Cancel
                    </Button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Add / Edit Single Trip Modal ───────────────────────────────── */}
      {[
        { open: isAddTripOpen, setOpen: setIsAddTripOpen, title: 'Add Production Trip', onSubmit: handleAddTrip },
        { open: isEditTripOpen, setOpen: (v) => { if (!v) { setIsEditTripOpen(false); setEditingTrip(null); resetTripForm(); } }, title: 'Edit Production Trip', onSubmit: handleEditTrip },
      ].map(({ open, setOpen, title, onSubmit }) => (
        <Dialog key={title} open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
            <form onSubmit={onSubmit} className="space-y-4" noValidate>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date *</Label>
                  <Input type="date" value={tripForm.date}
                    onChange={e => setTripForm({ ...tripForm, date: e.target.value })} required className="mt-1.5"
                    min={!user?.is_super_admin ? yesterday : undefined}
                    max={!user?.is_super_admin ? today : undefined}
                  />
                  {!user?.is_super_admin && <p className="text-xs text-amber-600 mt-1">Today or yesterday's date allowed</p>}
                </div>
                <div>
                  <Label>Shift *</Label>
                  <Select value={tripForm.shift} onValueChange={v => setTripForm({ ...tripForm, shift: v })}>
                    <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select shift" /></SelectTrigger>
                    <SelectContent>{SHIFTS.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Material Type *</Label>
                  <SearchableSelect
                    value={tripForm.material_type}
                    onValueChange={v => setTripForm({ ...tripForm, material_type: v })}
                    options={materialTypes.map(m => ({ value: m.name, label: `${m.name} (${m.cum_per_trip} m³/trip)` }))}
                    placeholder="Select material"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label>Number of Trips *</Label>
                  <Input type="number" min="1" value={tripForm.trips_count}
                    onChange={e => setTripForm({ ...tripForm, trips_count: parseInt(e.target.value) || 1 })}
                    required className="mt-1.5" />
                </div>
              </div>
              {/* Volume preview */}
              {tripForm.material_type && tripForm.trips_count && (
                <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2.5 text-sm">
                  <span className="text-slate-600">Calculated Volume: </span>
                  <span className="font-bold text-green-700">
                    {calcVolume(materialTypes, tripForm.material_type, tripForm.trips_count)} m³
                  </span>
                  <span className="text-slate-400 ml-2 text-xs">
                    ({materialTypes.find(m => m.name === tripForm.material_type)?.cum_per_trip} m³ × {tripForm.trips_count} trips)
                  </span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Dumper Door No. *</Label>
                  <SearchableSelect
                    value={tripForm.dumper_door_no}
                    onValueChange={v => setTripForm({ ...tripForm, dumper_door_no: v })}
                    options={getDumpers().map(a => ({ value: a.door_no, label: `${a.door_no} – ${a.make} ${a.model}` }))}
                    placeholder="Select dumper"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label>Excavator Door No.</Label>
                  <SearchableSelect
                    value={tripForm.excavator_door_no}
                    onValueChange={v => setTripForm({ ...tripForm, excavator_door_no: v })}
                    options={getExcavators().map(a => ({ value: a.door_no, label: `${a.door_no} – ${a.make} ${a.model}` }))}
                    placeholder="Select excavator"
                    className="mt-1.5"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Distance (km)</Label>
                  <Input type="number" step="0.1" value={tripForm.distance_km}
                    onChange={e => setTripForm({ ...tripForm, distance_km: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label>Start Time</Label>
                  <Input type="time" value={tripForm.start_time}
                    onChange={e => setTripForm({ ...tripForm, start_time: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label>End Time</Label>
                  <Input type="time" value={tripForm.end_time}
                    onChange={e => setTripForm({ ...tripForm, end_time: e.target.value })} className="mt-1.5" />
                </div>
              </div>
              <DialogFooter className="gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800"
                  data-testid={title.includes('Edit') ? 'edit-trip-submit-btn' : 'add-trip-submit-btn'}>
                  {title.includes('Edit') ? 'Save Changes' : 'Add Trip'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      ))}

      {/* ── Batch Entry Modal ──────────────────────────────────────────── */}
      <Dialog open={isBatchEntryOpen} onOpenChange={(v) => { if (!v) resetBatchForm(); setIsBatchEntryOpen(v); }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Batch Entry — Trips &amp; Hour Meter</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBatchEntry} className="space-y-5" noValidate>
            {/* Batch header */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Date *</Label>
                <Input type="date" value={batchForm.date}
                  onChange={e => setBatchForm({ ...batchForm, date: e.target.value })}
                  required className="mt-1.5"
                  min={!user?.is_super_admin ? yesterday : undefined}
                  max={!user?.is_super_admin ? today : undefined}
                />
                {!user?.is_super_admin && <p className="text-xs text-amber-600 mt-1">Today or yesterday's date allowed</p>}
              </div>
              <div>
                <Label>Shift *</Label>
                <Select value={batchForm.shift} onValueChange={v => setBatchForm({ ...batchForm, shift: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select shift" /></SelectTrigger>
                  <SelectContent>{SHIFTS.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
                <div>
                  <Label>Default Material (applies to all rows)</Label>
                  <SearchableSelect
                    value={batchForm.defaultMaterial || '__per_row__'}
                    onValueChange={applyDefaultMaterial}
                    options={[
                      { value: '__per_row__', label: '— Per row —' },
                      ...materialTypes.map(m => ({ value: m.name, label: m.name }))
                    ]}
                    placeholder="Select material"
                    className="mt-1.5"
                  />
                </div>
            </div>

            {/* Trip rows */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base font-semibold">Trips</Label>
                <Button type="button" variant="outline" size="sm"
                  onClick={() => setBatchForm({ ...batchForm, trips: [...batchForm.trips, emptyTripRow(batchForm.defaultMaterial)] })}>
                  <Plus className="h-4 w-4 mr-1" /> Add Row
                </Button>
              </div>
              <div className="border rounded-lg overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 text-xs">
                      <TableHead>Dumper</TableHead>
                      <TableHead>Excavator</TableHead>
                      <TableHead>Material</TableHead>
                      <TableHead className="text-center">Trips</TableHead>
                      <TableHead className="text-right text-green-700">Volume</TableHead>
                      <TableHead>Dist (km)</TableHead>
                      <TableHead>Start</TableHead>
                      <TableHead>End</TableHead>
                      <TableHead className="w-8" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {batchForm.trips.map((trip, idx) => {
                      const vol = calcVolume(materialTypes, trip.material_type, trip.trips_count);
                      return (
                        <TableRow key={idx}>
                          <TableCell className="p-1">
                            <SearchableSelect
                              value={trip.dumper_door_no}
                              onValueChange={v => updateBatchRow(idx, 'dumper_door_no', v)}
                              options={getDumpers().map(a => ({ value: a.door_no, label: a.door_no }))}
                              placeholder="Dumper"
                              className="w-28 h-8 text-xs"
                            />
                          </TableCell>
                          <TableCell className="p-1">
                            <SearchableSelect
                              value={trip.excavator_door_no}
                              onValueChange={v => updateBatchRow(idx, 'excavator_door_no', v)}
                              options={getExcavators().map(a => ({ value: a.door_no, label: a.door_no }))}
                              placeholder="Excavator"
                              className="w-28 h-8 text-xs"
                            />
                          </TableCell>
                          <TableCell className="p-1">
                            <SearchableSelect
                              value={trip.material_type}
                              onValueChange={v => updateBatchRow(idx, 'material_type', v)}
                              options={materialTypes.map(m => ({ value: m.name, label: m.name }))}
                              placeholder="Material"
                              className="w-32 h-8 text-xs"
                            />
                          </TableCell>
                          <TableCell className="p-1 text-center">
                            <Input type="number" min="1" value={trip.trips_count}
                              onChange={e => updateBatchRow(idx, 'trips_count', parseInt(e.target.value) || 1)}
                              className="w-16 h-8 text-center text-sm" data-testid={`batch-trips-${idx}`} />
                          </TableCell>
                          <TableCell className="p-1 text-right">
                            {vol !== null ? (
                              <span className="text-sm font-semibold text-green-700">{vol}</span>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </TableCell>
                          <TableCell className="p-1">
                            <Input type="number" step="0.1" value={trip.distance_km}
                              onChange={e => updateBatchRow(idx, 'distance_km', e.target.value)}
                              className="w-16 h-8 text-sm" />
                          </TableCell>
                          <TableCell className="p-1">
                            <Input type="time" value={trip.start_time}
                              onChange={e => updateBatchRow(idx, 'start_time', e.target.value)}
                              className="w-24 h-8 text-sm" />
                          </TableCell>
                          <TableCell className="p-1">
                            <Input type="time" value={trip.end_time}
                              onChange={e => updateBatchRow(idx, 'end_time', e.target.value)}
                              className="w-24 h-8 text-sm" />
                          </TableCell>
                          <TableCell className="p-1">
                            <div className="flex gap-0.5">
                              <Button type="button" variant="ghost" size="sm"
                                onClick={() => duplicateBatchRow(idx)}
                                className="text-slate-400 hover:text-blue-600 h-8 w-8 p-0"
                                title="Duplicate row">
                                <Copy className="h-3.5 w-3.5" />
                              </Button>
                              {batchForm.trips.length > 1 && (
                                <Button type="button" variant="ghost" size="sm"
                                  onClick={() => setBatchForm({ ...batchForm, trips: batchForm.trips.filter((_, i) => i !== idx) })}
                                  className="text-red-500 h-8 w-8 p-0">
                                  <XIcon className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              {/* Batch total */}
              {batchForm.trips.some(t => t.material_type && t.trips_count) && (
                <div className="mt-2 flex items-center justify-end gap-4 text-sm bg-slate-50 rounded-lg px-4 py-2.5">
                  <span className="text-slate-500">
                    Total Rows: <span className="font-semibold text-slate-800">{batchForm.trips.filter(t => t.dumper_door_no).length}</span>
                  </span>
                  <span className="text-slate-500">
                    Total Trips: <span className="font-semibold text-slate-800">
                      {batchForm.trips.reduce((sum, t) => sum + (parseInt(t.trips_count) || 0), 0)}
                    </span>
                  </span>
                  <span className="text-slate-500">
                    Total Volume: <span className="font-bold text-green-700">
                      {batchForm.trips.reduce((sum, t) => sum + (calcVolume(materialTypes, t.material_type, t.trips_count) || 0), 0).toFixed(2)} m³
                    </span>
                  </span>
                </div>
              )}
            </div>

            {/* Hour Meter Section */}
            <div className="border rounded-lg overflow-hidden">
              <button type="button"
                className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 text-sm font-medium"
                onClick={() => {
                  setShowHMSection(!showHMSection);
                  if (!showHMSection && batchForm.hour_meter_entries.length === 0) {
                    setBatchForm({ ...batchForm, hour_meter_entries: [emptyHMRow()] });
                  }
                }}>
                <span className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-600" />
                  Hour Meter Readings
                  {batchForm.hour_meter_entries.length > 0 && showHMSection && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                      {batchForm.hour_meter_entries.length} entry
                    </span>
                  )}
                </span>
                <span className="text-slate-400 text-xs">{showHMSection ? 'Hide ▲' : 'Expand ▼'}</span>
              </button>
              {showHMSection && (
                <div className="p-4 space-y-3">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 text-xs">
                        <TableHead>Asset Door No.</TableHead>
                        <TableHead>Start Reading (hrs)</TableHead>
                        <TableHead>End Reading (hrs)</TableHead>
                        <TableHead className="text-right text-blue-700">Hours Worked</TableHead>
                        <TableHead className="w-8" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {batchForm.hour_meter_entries.map((hm, idx) => {
                        const hrs = (hm.end_reading && hm.start_reading)
                          ? parseFloat((hm.end_reading - hm.start_reading).toFixed(1))
                          : null;
                        return (
                          <TableRow key={idx}>
                            <TableCell className="p-1">
                              <SearchableSelect
                                value={hm.asset_door_no}
                                onValueChange={v => updateHMRow(idx, 'asset_door_no', v)}
                                options={assets.map(a => ({ value: a.door_no, label: `${a.door_no} (${a.asset_type})` }))}
                                placeholder="Select asset"
                                className="w-36 h-8 text-xs"
                              />
                            </TableCell>
                            <TableCell className="p-1">
                              <Input type="number" step="0.1" value={hm.start_reading}
                                onChange={e => updateHMRow(idx, 'start_reading', e.target.value)}
                                className="w-28 h-8 text-sm" placeholder="e.g. 1540.5" />
                            </TableCell>
                            <TableCell className="p-1">
                              <Input type="number" step="0.1" value={hm.end_reading}
                                onChange={e => updateHMRow(idx, 'end_reading', e.target.value)}
                                className="w-28 h-8 text-sm" placeholder="e.g. 1548.0" />
                            </TableCell>
                            <TableCell className="p-1 text-right">
                              {hrs !== null ? (
                                <span className={`text-sm font-semibold ${hrs >= 0 ? 'text-blue-700' : 'text-red-600'}`}>{hrs} hrs</span>
                              ) : <span className="text-xs text-slate-400">—</span>}
                            </TableCell>
                            <TableCell className="p-1">
                              <Button type="button" variant="ghost" size="sm"
                                onClick={() => setBatchForm({ ...batchForm, hour_meter_entries: batchForm.hour_meter_entries.filter((_, i) => i !== idx) })}
                                className="text-red-500 h-8 w-8 p-0">
                                <XIcon className="h-3.5 w-3.5" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <Button type="button" variant="outline" size="sm"
                    onClick={() => setBatchForm({ ...batchForm, hour_meter_entries: [...batchForm.hour_meter_entries, emptyHMRow()] })}>
                    <Plus className="h-4 w-4 mr-1" /> Add Asset
                  </Button>
                </div>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => { setIsBatchEntryOpen(false); resetBatchForm(); }}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="batch-submit-btn">
                Save All
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Excavation Analytics ────────────────────────────────────────── */}
      <ProductionAnalyticsChart />

      {/* ── Import Modal ─────────────────────────────────────────────────── */}
      <Dialog open={isImportOpen} onOpenChange={setIsImportOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Import Production Data</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Upload Excel/CSV with columns:</p>
            <ul className="text-sm text-slate-500 list-disc list-inside">
              <li>date, shift, dumper_door_no, excavator_door_no</li>
              <li>material_type, distance_km, start_time, end_time, trips_count</li>
            </ul>
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
              <input type="file" accept=".xlsx,.xls,.csv" onChange={async (e) => {
                const file = e.target.files[0]; if (!file) return;
                setUploading(true);
                const fd = new FormData(); fd.append('file', file);
                try {
                  const res = await axios.post(`${API}/production/bulk-import`, fd, {
                    headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' }
                  });
                  toast.success(`Imported ${res.data.imported} trips!`);
                  if (res.data.errors?.length > 0) toast.error(`${res.data.errors.length} rows had errors`);
                  setIsImportOpen(false); fetchData();
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Import failed');
                } finally { setUploading(false); }
              }} disabled={uploading} className="hidden" id="production-file" />
              <label htmlFor="production-file" className="cursor-pointer">
                {uploading ? <Loader2 className="h-8 w-8 mx-auto text-slate-400 animate-spin" /> : <Upload className="h-8 w-8 mx-auto text-slate-400" />}
                <p className="mt-2 text-sm text-slate-600">{uploading ? 'Uploading...' : 'Click to upload'}</p>
              </label>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Request Dialog */}
      <DeleteRequestDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        module="production"
        entryId={deleteTarget?.id}
        entryDisplay={deleteTarget?.display}
        onSuccess={() => setDeleteTarget(null)}
      />

      {/* Trip Card Scanner */}
      <TripCardScanner
        isOpen={isScannerOpen}
        onClose={() => setIsScannerOpen(false)}
        onSaved={() => { fetchData(); }}
        materialTypes={materialTypes.map(m => m.name)}
      />
    </div>
  );
};

// Shift breakdown card
const ShiftBreakdown = ({ shifts }) => {
  if (!shifts) return null;
  return (
    <div className="grid grid-cols-3 gap-4">
      {SHIFTS.map(shift => (
        <div key={shift.id} className={`bg-white rounded-lg border p-4 border-l-4 ${
          shift.id === 'A' ? 'border-l-blue-500' : shift.id === 'B' ? 'border-l-green-500' : 'border-l-purple-500'
        }`}>
          <h4 className="font-medium text-slate-900">{shift.label}</h4>
          <div className="mt-2 space-y-1">
            <p className="text-sm text-slate-600">Trips: <span className="font-semibold">{shifts[shift.id]?.trips || 0}</span></p>
            <p className="text-sm text-slate-600">Volume: <span className="font-semibold">{shifts[shift.id]?.cum || 0} m³</span></p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ProductionPage;
